import { createColumnHelper } from "@tanstack/react-table"
import { CountryCell, TextCell } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { SupplierProductionSiteDto } from "~/gen/api"
import { COUNTRIES } from "~/utils/countries"
import { formatAddress } from "~/utils/format-address"

const columnHelper = createColumnHelper<SupplierProductionSiteDto>()

export const useColumns = (prodsiteFieldKeys: string[]) => {
  const { t } = useTranslation("suppliers")

  return useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        enablePinning: true,
        enableSorting: false,
        header: t("productionSites.columns.name"),
        cell: (info) => <TextCell value={info.getValue() ?? undefined} />,
      }),
      columnHelper.accessor("country", {
        id: "country",
        enableSorting: false,
        header: t("productionSites.columns.country"),
        cell: (info) => {
          const country = info.getValue()
          const address = formatAddress(info.row.original)

          return (
            <CountryCell
              tooltip={typeof address === "string" ? address : undefined}
              countryIso3166={country}
              w="100%"
              countriesMapper={COUNTRIES}
            />
          )
        },
      }),
      columnHelper.accessor("email", {
        id: "supplier",
        enableSorting: false,
        header: t("productionSites.columns.contactEmail"),
        cell: ({ row }) => {
          if (row.original.confidential) {
            return (
              <TextCell value={t("productionSites.columns.confidential")} />
            )
          }
          return <TextCell value={row.original.email ?? undefined} />
        },
      }),
      columnHelper.accessor("internalCode", {
        id: "internalCode",
        enableSorting: false,
        header: t("productionSites.columns.internalCode"),
        cell: (info) => <TextCell value={info.getValue() ?? undefined} />,
      }),
      columnHelper.accessor("registrationIds", {
        id: "registrationId",
        enableSorting: false,
        header: t("productionSites.columns.registrationId"),
        cell: (info) => (
          <TextCell
            value={(info.getValue() ?? [])
              .map((r) => r.registrationId)
              .join(", ")}
          />
        ),
      }),
      columnHelper.accessor("performedActivities", {
        id: "performedActivities",
        enableSorting: false,
        header: t("productionSites.columns.performedActivities"),
        cell: (info) => <TextCell value={(info.getValue() ?? []).join(", ")} />,
      }),
      ...Object(
        prodsiteFieldKeys.map((prodsiteFieldKey) => {
          return columnHelper.accessor(
            (row) => {
              return (row.prodSiteFields ?? []).find(
                (psf) => psf.key === prodsiteFieldKey
              )?.value
            },
            {
              id: prodsiteFieldKey + "-id",
              header: prodsiteFieldKey,
              enableSorting: false,
              cell: (info) => <TextCell value={info.getValue()} />,
            }
          )
        })
      ),
    ],
    [t, prodsiteFieldKeys]
  )
}
